import axiosInstance from '../axios/axios-instance';
import { SignedTileDto } from 'shared/dto/project/signed-tile.dto';
import { TileClickResponseDto } from 'shared/enums/project/tile-click-response.dto';
import { DynamicProfilerPostalCodeValidationResponseDto } from 'shared/interfaces/dynamic-profiler/dynamic-profiler-postal-code-validation-response.dto';
import { GetDynamicProfilerResponseDto } from 'shared/interfaces/dynamic-profiler/get-dynamic-profiler-response.dto';

export const dynamicProfilerApi = {
  /**
   * Submit the dynamic profiler answer.
   * @param dynamicProfilerId - The ID of the dynamic profiler.
   * @param values - The values of the dynamic profiler.
   * @param skippedQuestions - The skipped questions with reasons.
   */
  async submitDynamicProfilerAnswers(
    dynamicProfilerId: number,
    values: Record<string, unknown>,
    skippedQuestions?: Array<{ questionId: number; reasonId: number; comment?: string }>,
    isFirstDynamicProfiler?: boolean
  ): Promise<void> {
    await axiosInstance.post(`/dynamic-profiler/${dynamicProfilerId}`, {
      answers: values,
      skippedQuestions,
      isFirstDynamicProfiler,
    });
  },

  /**
   * Get the dynamic profiler questions.
   * @returns The dynamic profiler questions.
   */
  async getFirstDynamicProfiler(): Promise<GetDynamicProfilerResponseDto> {
    const response = await axiosInstance.get('/dynamic-profiler/first-dynamic-profiler-questions');
    return response.data as GetDynamicProfilerResponseDto;
  },

  /**
   * Validate the postal code.
   * @param postalCode - The postal code to validate.
   * @returns The validation response.
   */
  async validatePostalCode(postalCode: string): Promise<DynamicProfilerPostalCodeValidationResponseDto> {
    const response = await axiosInstance.get(`/dynamic-profiler/validate-postal-code?postalCode=${postalCode}`);
    return response.data as DynamicProfilerPostalCodeValidationResponseDto;
  },

  /**
   * Processes the dynamic profiler completion.
   * @param signedTileDto The signed tile DTO (containing original tile details and signature).
   * @returns A promise that resolves to the result of the processing.
   */
  async processDynamicProfilerCompletion(signedTileDto: SignedTileDto): Promise<TileClickResponseDto> {
    const response = await axiosInstance.post<TileClickResponseDto>(
      '/dynamic-profiler/process-dynamic-profiler-completion',
      signedTileDto,
      {
        headers: {
          'x-rewardbloom-signature': signedTileDto.signature,
        },
      }
    );
    return response.data;
  },
};
